import React, { useEffect, useMemo, useRef } from "react"
import Select from "react-select"

export interface ServiceOption {
  service_id: number
  stripe_product_id?: string
  service_details?: {
    title?: string
    availability_rule_id?: number
    status?: string
  }
  title?: string
  status?: string
}

interface MultiSelectServicesProps {
  options: ServiceOption[]
  selected: ServiceOption[]
  onChange: (selected: ServiceOption[]) => void
  availability?: any
  matchingServices?: ServiceOption[]
  setIsUnsavedCalendar?: (val: boolean) => void
}

const MultiSelectServices: React.FC<MultiSelectServicesProps> = ({
  options,
  selected,
  onChange,
  availability,
  matchingServices,
  setIsUnsavedCalendar,
}) => {
  const isDefault = availability?.is_default === 1
  const activeOptions = options.filter(
    (o) => o.status === "active" || o.service_details?.status === "active"
  )

  const servicesWithMatchingAvailability = useMemo(() => {
    return activeOptions.filter(
      (o) => o.service_details?.availability_rule_id === availability.id
    )
  }, [activeOptions, availability.id])

  const selectOptions = activeOptions.map((o) => ({
    value: o.service_id,
    label: o.service_details?.title || o.title,
  }))

  const selectedOptions = selected.map((s) => ({
    value: s.service_id,
    label: s.service_details?.title || s.title,
  }))

  const isInitialMount = useRef(true)

  useEffect(() => {
    if (isInitialMount.current && servicesWithMatchingAvailability.length > 0) {
      const initialSelection = [
        ...selected,
        ...servicesWithMatchingAvailability,
      ]
      const uniqueInitialSelection = Array.from(
        new Set(initialSelection.map((s) => s.service_id))
      ).map((id) => initialSelection.find((s) => s.service_id === id)!)

      onChange(uniqueInitialSelection)
      isInitialMount.current = false
    }
  }, [servicesWithMatchingAvailability, selected, onChange])

  return (
    <div className="w-full xl:w-[75%] rounded">
      <Select
        options={selectOptions}
        value={selectedOptions}
        onChange={(vals, actionMeta) => {
          let userSelection: ServiceOption[] = []

          if (actionMeta.action === "clear") {
            const preserved = selected.filter(
              (s: ServiceOption) =>
                isDefault &&
                matchingServices?.some(
                  (m: ServiceOption) => m.service_id === s.service_id
                )
            )
            userSelection = preserved
          } else {
            userSelection = (vals || []).map(
              (v: any) => options.find((o) => o.service_id === v.value)!
            )
          }

          onChange(userSelection)

          const initialIds = servicesWithMatchingAvailability.map(
            (s) => s.service_id
          )
          const updatedIds = userSelection.map((s) => s.service_id)
          const hasChanged =
            updatedIds.length !== initialIds.length ||
            updatedIds.some((id) => !initialIds.includes(id))

          setIsUnsavedCalendar?.(hasChanged)
        }}
        isMulti
        closeMenuOnSelect={false}
        isClearable={true}
        onKeyDown={(e) => {
          if (e.key === "Backspace" || e.key === "Delete") {
            e.preventDefault()
          }
        }}
        styles={{
          container: (base) => ({ ...base, width: "100%" }),
          control: (base) => ({ ...base, minHeight: 50, fontSize: 16 }),
          option: (base, { isFocused, isSelected }) => ({
            ...base,
            fontSize: 16,
            padding: "12px 20px",
            backgroundColor: isFocused
              ? "#4750F5"
              : isSelected
              ? "#4750F5"
              : "white",
            color: isFocused || isSelected ? "white" : "black",
            cursor: "pointer",
          }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: "#4750F5",
            height: 40,
            display: "flex",
            alignItems: "center",
            padding: "0 8px",
            borderRadius: 20,
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: "white",
            fontSize: 16,
            lineHeight: "40px",
            display: "flex",
            alignItems: "center",
          }),
          multiValueRemove: (base, state) => {
            const serviceId = state.data.value
            const isMatching = matchingServices?.some(
              (s: ServiceOption) => s.service_id === serviceId
            )

            if (isDefault && isMatching) {
              return { ...base, display: "none" }
            }

            return {
              ...base,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 40,
              width: 24,
              ":hover": {
                backgroundColor: "#1050CA",
                color: "white",
              },
              borderRadius: "0 20px 20px 0",
            }
          },
          valueContainer: (base) => ({
            ...base,
            padding: "6px 12px",
          }),
        }}
        menuPortalTarget={document.body}
      />
    </div>
  )
}

export default MultiSelectServices
